import * as R from 'ramda';

import { PosPlugin } from 'plugins/plugin';
import {
  getProductInOrderByIndex,
  getProductsInShoppingCart,
} from 'reducers/ShoppingCart';
import {
  getGivexConfiguration,
  getHasGivexProductInCart,
} from 'plugins/givexHeartland/configuration/Configuration';
import { addError } from 'actions/Error';
import { CartItem } from 'containers/Forms/GiftCardSerial/types';

import { checkIfGivexIsAllowedBasedOnAp } from '../utils';

/**
 * Removes givex refill payments from the payments list (so that the totals would add up), and converts givex charges to be of type giftcard
 */
export const postProcessPayments: Required<
  PosPlugin
>['onSaveSalesDocument']['on'] = (p, requests) => async (
  dispatch,
  getState,
) => {
  const isGivexPayment = R.both(
    R.propEq('requestName', 'savePayment'),
    R.propEq('cardType', 'GIVEX'),
  );
  const isGivexRefillPayment = R.allPass([
    R.propEq('requestName', 'savePayment'),
    R.propEq('type', 'META'),
  ]);
  return R.pipe(
    // Remove refill payments from payments list
    R.filter(R.complement(isGivexRefillPayment)),
    // Convert charges to type GIFTCARD
    R.map(R.when(isGivexPayment, R.assoc('type', 'GIFTCARD'))),
  )(requests) as typeof requests;
};

export const disableGiftcardTenderAndRemoveGivexPayment: Required<
  PosPlugin
>['onOpenPaymentModal']['on'] = (p, ap) => async (dispatch, getState) => {

  let newAp = ap;
  const withoutGiftcards = R.map(
    R.when(
      ({ type }) => type === 'GIFTCARD',
      R.mergeDeepLeft({
        amount: -1,
        total: -1,
      }),
    ),
  );

  const withoutRegularGiftcards = R.map(
    R.when(
      ({ type, serial }) => type === 'GIFTCARD' && !serial,
      R.mergeDeepLeft({
        amount: -1,
        total: -1,
      }),
    ),
  );

  if (getHasGivexProductInCart(getState())) {
    newAp = R.evolve({
      paymentLimits: withoutGiftcards,
    })(newAp);
  } else {
    newAp = R.evolve({
      paymentLimits: withoutRegularGiftcards,
    })(newAp);
  }
  const isGivexAllowed = checkIfGivexIsAllowedBasedOnAp(newAp)(getState());
  if (!isGivexAllowed) {
    const withoutGivex = (
      payments: Record<string, { paymentIntegration: string }>,
    ) => {
      const filteredPayments = Object.entries(payments).filter(
        ([_, payment]) => payment.paymentIntegration !== 'givex',
      );
      return Object.fromEntries(filteredPayments);
    };
    newAp = R.evolve({ payments: withoutGivex })(newAp);
  }
  return newAp;
};

/**
 * Add locked negative 'refill' payments for each gift card in cart,
 * and subtract that amount from the total so the balance would remain unaffected
 */
export const addGivexRefillPaymentsFromCart: Required<
  PosPlugin
>['onOpenPaymentModal']['on'] = (p, ap) => async (_dispatch, getState) => {
  const { giftcardProductID } = getGivexConfiguration(getState());
  const cards: CartItem[] = getProductsInShoppingCart(getState()).filter(
    p => p.productID === giftcardProductID,
  );
  const refillAmounts = cards.map(p => Number(p.finalPriceWithVAT));

  const refillData = cards.map(p => ({
    amount: Number(p.finalPriceWithVAT),
    uuid: p.uuid,
    rowNumber: p.rowNumber,
  }));

  const payments = refillData.map(({ amount, uuid, rowNumber }) => ({
    produuid: uuid,
    rowNumber,
    locked: true,
    type: 'META',
    paymentIntegration: 'givex',
    amount: (-amount).toFixed(2),
    caption: 'GIVEX refill',
  }));

  const newAP = R.evolve({
    total: R.subtract(R.__, R.sum(refillAmounts)),
    shoppingCartTotal: R.subtract(R.__, R.sum(refillAmounts)),
    payments: R.mergeDeepLeft(
      R.fromPairs(payments.map(pmt => [pmt.produuid, pmt])),
    ),
  } as any)(ap) as typeof ap;
  return newAP;
};

export const convertOriginalGiftcardPaymentsBackToGivex: Required<
  PosPlugin
>['onSetCurrentSalesDocPayments']['on'] = (p, ap) => async (
  dispatch,
  getState,
) =>
  R.evolve({
    original: R.map(
      R.when(
        (p: any) => p.cardType === 'GIVEX',
        R.pipe(
          R.assoc('type', 'CARD'),
          R.assoc('paymentIntegration', 'givex'),
          R.assoc('caption', 'GIVEX'),
        ),
      ),
    ),
  })(ap);

/**
 * Sorts grouped payments so that givex refill is processed the last.
 * This avoids refilling Givex card before customer pays for it.
 */
export const makeGivexRefillLastPayment: Required<
  PosPlugin
>['onProcessPayments']['on'] = (p, ap) => async () => {
  const hasGivexRefills = payments =>
    !!payments.find(p => p.caption === 'GIVEX refill');
  return R.sort(([_, prev], [__, curr]) => {
    if (hasGivexRefills(prev)) return 1;
    if (hasGivexRefills(curr)) return -1;
    return 0;
  })(ap);
};

export const applyGivexCardBalance: Required<
  PosPlugin
>['onSetPayment']['on'] = (p, ap) => async () => {
  if (ap.paymentIntegration !== 'givex') return ap;
  const applyBalance = amount => {
    const cardBalance = !ap.GIVEX
      ? ap.original.certificateBalance
      : ap.GIVEX.card.balance;
    return Number(amount) > Number(cardBalance) ? cardBalance : amount;
  };
  return R.evolve({ amount: applyBalance })(ap);
};

export const restrictReturningGivexIncrementProduct: Required<
  PosPlugin
>['onUpdateOrderAmount']['before'] = p => async (dispatch, getState) => {
  const { isAllowedOnReturnWithoutReceipt } = getGivexConfiguration(getState());
  if (isAllowedOnReturnWithoutReceipt || p.amount >= 0) return;
  const order = getProductInOrderByIndex(p.orderIndex)(getState());
  const { giftcardProductID } = getGivexConfiguration(getState());
  if (order.productID !== giftcardProductID) return;
  dispatch(
    addError('Returning Givex increment without receipt is not allowed'),
  );
  throw new Error('Returning Givex increment without receipt is not allowed');
};
